// voice.js — Chat VOCAL WebRTC (mesh P2P) pour VertinGo.
// DÉSACTIVÉ tant que /voice/config renvoie enabled:false (pas de TURN provisionné = pas de vocal).
// Signaling transport-agnostique : l'appelant fournit sendSignal() et pousse les messages via onSignal().
const BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

let _cfg = null;
export async function getVoiceConfig() {
  if (_cfg) return _cfg;
  try { const r = await fetch(`${BASE}/voice/config`); _cfg = await r.json(); }
  catch { _cfg = { enabled: false, iceServers: [] }; }
  return _cfg;
}
export async function voiceEnabled() {
  try { return !!(await getVoiceConfig()).enabled; } catch { return false; }
}

// Mesh vocal : chaque pair se connecte à tous les autres. Signaling via sendSignal (relayé par le
// match Nakama / un canal). Messages : {v:'hello'|'hi'|'offer'|'answer'|'ice'|'bye', from, to?, ...}.
export class VoiceRoom {
  constructor({ selfId, sendSignal, iceServers }) {
    this.selfId = String(selfId);
    this.sendSignal = sendSignal;
    this.iceServers = (iceServers && iceServers.length) ? iceServers : [{ urls: 'stun:stun.l.google.com:19302' }];
    this.peers = new Map();   // id -> RTCPeerConnection
    this.audios = new Map();  // id -> HTMLAudioElement
    this.stream = null;
    this.micMuted = false;
    this.outMuted = false;
    this.onPeers = null;      // callback([ids]) quand la liste de pairs change
  }

  async start() {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    if (this.micMuted) this.stream.getAudioTracks().forEach((t) => { t.enabled = false; });
    this.sendSignal({ v: 'hello', from: this.selfId });   // annonce → les présents répondent
    return true;
  }

  _notify() { if (this.onPeers) this.onPeers([...this.peers.keys()]); }

  _pc(id) {
    let pc = this.peers.get(id);
    if (pc) return pc;
    pc = new RTCPeerConnection({ iceServers: this.iceServers });
    if (this.stream) this.stream.getTracks().forEach((t) => pc.addTrack(t, this.stream));
    pc.onicecandidate = (e) => { if (e.candidate) this.sendSignal({ v: 'ice', from: this.selfId, to: id, candidate: e.candidate }); };
    pc.ontrack = (e) => {
      let a = this.audios.get(id);
      if (!a) { a = new Audio(); a.autoplay = true; this.audios.set(id, a); }
      a.srcObject = e.streams[0]; a.muted = this.outMuted;
      const p = a.play(); if (p && p.catch) p.catch(() => {});
    };
    pc.onconnectionstatechange = () => {
      if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) this._drop(id);
    };
    this.peers.set(id, pc);
    this._notify();
    return pc;
  }

  async _offer(id) {
    const pc = this._pc(id);
    const o = await pc.createOffer();
    await pc.setLocalDescription(o);
    this.sendSignal({ v: 'offer', from: this.selfId, to: id, sdp: pc.localDescription });
  }

  // Anti-glare : le pair au plus grand id initie l'offre (déterministe des deux côtés).
  _maybeOffer(other) { if (this.selfId > String(other)) this._offer(other); else this._pc(other); }

  async onSignal(m) {
    if (!m || !m.from || String(m.from) === this.selfId) return;
    if (m.to && String(m.to) !== this.selfId) return;
    try {
      if (m.v === 'hello') { this.sendSignal({ v: 'hi', from: this.selfId, to: m.from }); this._maybeOffer(m.from); }
      else if (m.v === 'hi') { this._maybeOffer(m.from); }
      else if (m.v === 'offer') {
        const pc = this._pc(m.from);
        await pc.setRemoteDescription(m.sdp);
        const a = await pc.createAnswer();
        await pc.setLocalDescription(a);
        this.sendSignal({ v: 'answer', from: this.selfId, to: m.from, sdp: pc.localDescription });
      } else if (m.v === 'answer') {
        const pc = this.peers.get(m.from); if (pc) await pc.setRemoteDescription(m.sdp);
      } else if (m.v === 'ice') {
        const pc = this.peers.get(m.from); if (pc && m.candidate) { try { await pc.addIceCandidate(m.candidate); } catch { /* */ } }
      } else if (m.v === 'bye') { this._drop(m.from); }
    } catch { /* résilient : une erreur de signaling ne casse pas la session */ }
  }

  _drop(id) {
    const pc = this.peers.get(id); if (pc) { try { pc.close(); } catch { /* */ } }
    this.peers.delete(id);
    const a = this.audios.get(id); if (a) { try { a.srcObject = null; } catch { /* */ } }
    this.audios.delete(id);
    this._notify();
  }

  muteMic(b) { this.micMuted = !!b; if (this.stream) this.stream.getAudioTracks().forEach((t) => { t.enabled = !b; }); }
  muteOutput(b) { this.outMuted = !!b; this.audios.forEach((a) => { a.muted = !!b; }); }

  stop() {
    try { this.sendSignal({ v: 'bye', from: this.selfId }); } catch { /* */ }
    this.peers.forEach((pc) => { try { pc.close(); } catch { /* */ } });
    this.peers.clear();
    this.audios.forEach((a) => { try { a.srcObject = null; } catch { /* */ } });
    this.audios.clear();
    if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this._notify();
  }
}
